import { Button } from '@chakra-ui/react';
import { ReactComponent as PlusSvgIcon } from 'assets/icons/plus.svg';
import { INVITE_TEAM_REDIRECT_LINK, UPGRADE_REDIRECT_BIZ_LINK } from 'constants/links';
import { useVenngageE1Gate } from 'hooks/useVenngageE1Gate';
import { UserType, UserTypes } from 'modules/User';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const DISABLE_INVITE_TEAM_USER_TYPES: UserType[] = [UserTypes.Education];

const generateLink = (userType: UserType) => {
  let link = '';

  switch (userType) {
    case UserTypes.Free:
    case UserTypes.Premium:
      link = UPGRADE_REDIRECT_BIZ_LINK;
      break;
    case UserTypes.Enterprise:
      link = INVITE_TEAM_REDIRECT_LINK;
      break;
    default:
      break;
  }

  return link;
};

type InviteTeamButtonProps = {
  userType?: UserType;
  // Will only be called when for business users
  onClick?: () => void;
  withIcon?: boolean;
};
/**
 * Invite team button
 *
 * - Free User & Premium User: Redirect to => '/account/subscription/business'
 * - Education User: N/A
 * - Business Owner & Member: Display Invite them member modal
 * - Enterprise: Redirect to '/infographics/my_team'
 */
export const InviteTeamButton = memo(({ userType, onClick, withIcon = false }: InviteTeamButtonProps) => {
  const { venngageE2 } = useVenngageE1Gate();
  const { t } = useTranslation('common_navbar');

  const showButton = useMemo(
    () => venngageE2?.experiments?.teamshare === true && userType && !DISABLE_INVITE_TEAM_USER_TYPES.includes(userType),
    [venngageE2, userType],
  );

  if (!showButton) return null;

  const testId = 'navbar-invite-team-button';
  const buttonText = t('navbar.invite-team-button');
  const isBusinessUser = userType === UserTypes.Business;

  if (isBusinessUser) {
    return (
      <Button
        variant='upgrade-outline'
        size='sm'
        data-testid={testId}
        onClick={onClick}
        leftIcon={withIcon ? <PlusSvgIcon stroke='var(--vg-colors-upgrade-blue-500)' /> : undefined}
      >
        {buttonText}
      </Button>
    );
  }

  // else it's a link that redirects to the subscription page
  const link = generateLink(userType!);
  return (
    <Button
      as='a'
      href={link}
      variant='upgrade-outline'
      size='sm'
      data-testid={testId}
      leftIcon={withIcon ? <PlusSvgIcon stroke='var(--vg-colors-upgrade-blue-500)' /> : undefined}
    >
      {buttonText}
    </Button>
  );
});

InviteTeamButton.displayName = 'InviteTeamButton';
